/**
 * Earn Rules Context
 *
 * Provides shared state and configuration to EarnRuleCard components
 * to eliminate prop drilling.
 */
import { createContext, useContext, type FC, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

/**
 * Claimed reward data
 */
export interface ClaimedReward {
	earn_rule_id: string;
}

/**
 * Button text customizations
 */
export interface ButtonTexts {
	join: string;
	signIn: string;
	claim: string;
	claimed: string;
	automatic: string;
}

/**
 * Context value provided to all child components
 */
export interface EarnRulesContextValue {
	/** User's claimed rewards */
	claimedRewards: ClaimedReward[];

	/** Credits currency name (e.g., 'points') */
	creditsCurrency: string;

	/** Heading level for card labels (1-6) */
	labelLevel: number;

	/** Whether the current user is a guest */
	isGuest: boolean;

	/** Callback when a reward is successfully claimed */
	onClaimSuccess: () => void;

	/** Customizable button texts */
	buttonTexts: ButtonTexts;

	/** Inline styles for buttons from custom colors (optional) */
	buttonInlineStyles?: React.CSSProperties;

	/** Whether to show credits as a badge */
	showCreditsBadge: boolean;

	/** Handler for joining the program (optional, only for logged-in not enrolled) */
	onJoinProgram?: () => void;

	/** Loading state for join program (optional) */
	isJoiningProgram?: boolean;

	/** Error message for join program (optional) */
	joinProgramError?: string | null;
}

/**
 * Props for the EarnRulesProvider component
 */
export interface EarnRulesProviderProps {
	children: ReactNode;
	value: EarnRulesContextValue;
}

// ============================================================================
// Context
// ============================================================================

const EarnRulesContext = createContext<EarnRulesContextValue | undefined>(undefined);

/**
 * Provider component for earn rules context
 *
 * @example
 * ```tsx
 * <EarnRulesProvider value={{ claimedRewards, creditsCurrency, labelLevel, isGuest, onClaimSuccess }}>
 *   <EarnRuleCard rule={rule} />
 * </EarnRulesProvider>
 * ```
 */
export const EarnRulesProvider: FC<EarnRulesProviderProps> = ({ children, value }) => {
	return <EarnRulesContext.Provider value={value}>{children}</EarnRulesContext.Provider>;
};

/**
 * Hook to access earn rules context
 *
 * @throws {Error} If used outside of EarnRulesProvider
 *
 * @example
 * ```tsx
 * const { claimedRewards, creditsCurrency, labelLevel } = useEarnRulesContext();
 * ```
 */
export function useEarnRulesContext(): EarnRulesContextValue {
	const context = useContext(EarnRulesContext);

	if (context === undefined) {
		throw new Error('useEarnRulesContext must be used within an EarnRulesProvider');
	}

	return context;
}
